import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, TrendingUp, Activity, Heart, AlertTriangle, Award, BarChart3, Target, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend } from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';
import { API } from '../config/api';

const COLORS = ['#02b5ff', '#ff025f', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export default function TeamAggregateDashboard({ teamName }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const fetchAggregateStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch all children and aggregate stats
      const [childrenRes, medicalRes] = await Promise.all([
        axios.get(`${API}/children`, { headers }),
        axios.get(`${API}/medical/summary`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      const children = childrenRes.data;
      const filteredChildren = teamName 
        ? children.filter(c => c.current_team_name === teamName || c.assigned_coach_name === teamName)
        : children;
      
      // Calculate aggregate stats
      const totalAthletes = filteredChildren.length;
      
      // Age distribution
      const ageGroups = { '6-8': 0, '9-11': 0, '12-14': 0, '15-18': 0, '18+': 0 };
      filteredChildren.forEach(child => {
        if (child.date_of_birth) {
          const age = calculateAge(child.date_of_birth);
          if (age <= 8) ageGroups['6-8']++;
          else if (age <= 11) ageGroups['9-11']++;
          else if (age <= 14) ageGroups['12-14']++;
          else if (age <= 18) ageGroups['15-18']++;
          else ageGroups['18+']++;
        }
      });
      
      // Sport distribution
      const sportCounts = {};
      filteredChildren.forEach(child => {
        const sport = child.primary_sport || 'Unspecified';
        sportCounts[sport] = (sportCounts[sport] || 0) + 1;
      });
      
      // Medical status
      const pendingMedical = filteredChildren.filter(c => !c.medical_completed).length;
      const completedMedical = totalAthletes - pendingMedical;
      
      // Measurement status
      const withMeasurements = filteredChildren.filter(c => c.last_measurement_date).length;
      const withoutMeasurements = totalAthletes - withMeasurements;
      
      // Test status
      const withTests = filteredChildren.filter(c => c.last_test_date).length;
      const withoutTests = totalAthletes - withTests;
      
      setStats({
        totalAthletes,
        ageDistribution: Object.entries(ageGroups).map(([name, value]) => ({ name, value })).filter(d => d.value > 0),
        sportDistribution: Object.entries(sportCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6),
        medicalStatus: [
          { name: 'Completed', value: completedMedical },
          { name: 'Pending', value: pendingMedical }
        ],
        measurementStatus: { with: withMeasurements, without: withoutMeasurements },
        testStatus: { with: withTests, without: withoutTests },
        genderSplit: calculateGenderSplit(filteredChildren)
      });
    } catch (err) {
      console.error('Failed to load aggregate stats:', err);
    } finally {
      setLoading(false);
    }
  }, [teamName]);

  useEffect(() => {
    fetchAggregateStats();
  }, [fetchAggregateStats]);

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const calculateGenderSplit = (children) => {
    const counts = { Male: 0, Female: 0, Other: 0 };
    children.forEach(c => {
      const gender = c.gender?.toLowerCase();
      if (gender === 'male') counts.Male++;
      else if (gender === 'female') counts.Female++;
      else counts.Other++;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#02b5ff]" />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6" data-testid="team-aggregate-dashboard">
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer mb-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-xl flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-[#02b5ff]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Team Overview</h3>
            <p className="text-xs text-zinc-500">{stats.totalAthletes} athletes • Aggregate statistics</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-zinc-500" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}
      </div>

      {expanded && (
        <div className="space-y-6">
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
              <Users className="h-6 w-6 text-[#02b5ff] mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{stats.totalAthletes}</div>
              <div className="text-xs text-zinc-500">Total Athletes</div>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
              <Activity className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{stats.measurementStatus.with}</div>
              <div className="text-xs text-zinc-500">With Measurements</div>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
              <Target className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{stats.testStatus.with}</div>
              <div className="text-xs text-zinc-500">With Tests</div>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
              <Heart className={`h-6 w-6 mx-auto mb-2 ${stats.medicalStatus[1]?.value > 0 ? 'text-[#ff025f]' : 'text-green-500'}`} />
              <div className="text-2xl font-bold text-white">
                {stats.medicalStatus[1]?.value > 0 ? stats.medicalStatus[1].value : '✓'}
              </div>
              <div className="text-xs text-zinc-500">
                {stats.medicalStatus[1]?.value > 0 ? 'Pending Medical' : 'Medical Complete'}
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Age Distribution */}
            <div className="bg-zinc-800/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#02b5ff]" />
                Age Distribution
              </h4>
              <div className="h-48">
                <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={stats.ageDistribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" tick={{ fill: '#888', fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fill: '#888', fontSize: 11 }} width={50} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="value" fill="#02b5ff" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </SafeResponsiveContainer>
              </div>
            </div>

            {/* Sport Distribution */}
            <div className="bg-zinc-800/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Award className="h-4 w-4 text-[#ff025f]" />
                Sport Distribution
              </h4>
              <div className="h-48">
                <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <PieChart>
                    <Pie
                      data={stats.sportDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {stats.sportDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '10px' }}
                      formatter={(value) => <span style={{ color: '#888' }}>{value}</span>}
                    />
                  </PieChart>
                </SafeResponsiveContainer>
              </div>
            </div>

            {/* Gender & Status */}
            <div className="bg-zinc-800/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-500" />
                Gender Split
              </h4>
              <div className="h-48">
                <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <RadarChart data={stats.genderSplit}>
                    <PolarGrid stroke="#333" />
                    <PolarAngleAxis dataKey="name" tick={{ fill: '#888', fontSize: 11 }} />
                    <Radar
                      name="Athletes"
                      dataKey="value"
                      stroke="#02b5ff"
                      fill="#02b5ff"
                      fillOpacity={0.3}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    />
                  </RadarChart>
                </SafeResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-zinc-800/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Measurement Completion</span>
                <span className="text-sm font-medium text-white">
                  {Math.round((stats.measurementStatus.with / stats.totalAthletes) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#02b5ff] to-[#0090cc] rounded-full transition-all"
                  style={{ width: `${(stats.measurementStatus.with / stats.totalAthletes) * 100}%` }}
                />
              </div>
            </div>
            <div className="bg-zinc-800/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Test Completion</span>
                <span className="text-sm font-medium text-white">
                  {Math.round((stats.testStatus.with / stats.totalAthletes) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#ff025f] to-[#dd0050] rounded-full transition-all"
                  style={{ width: `${(stats.testStatus.with / stats.totalAthletes) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
